import { publicBaseDomain } from "./publicUrl";

function normalizeHost(raw: string): string {
  return raw.split(":")[0]!.toLowerCase();
}

// ——— Bare-host stacks (no base domain configured; development) ———
//
// Multitenancy without subdomain isolation: each tenant's public_domain is
// a complete hostname (`localhost`, `acme.localhost`), not a label under a
// shared base. The host is the stored value itself, and tenant origins keep
// the stack UI URL's scheme and port with the tenant's hostname swapped in.

// An IP literal never names a tenant — that's how development's internal
// zero-cache address (127.0.0.1) falls through to the Origin fallback.
function bareHostTenantDomain(hostname: string): string | null {
  if (/^\d+(\.\d+){3}$/.test(hostname)) return null;
  return hostname || null;
}

function bareHostTenantUiOrigin(
  externalUiUrl: string,
  tenantPublicDomain: string
): string {
  const url = new URL(externalUiUrl);
  url.hostname = tenantPublicDomain;
  return url.origin;
}

// ——— Base-domain stacks (subdomain isolation; cloud) ———

// Strip the universal base suffix from a host to get the tenant subdomain
// label stored in tenants.public_domain. With a base (subdomain isolation
// on) the label is the last DNS label before the base, so `acme.cap.3b.run`
// minus base `3b.run` yields `cap` — this also covers a bare `cap.3b.run`
// with no slug. Returns null when the host doesn't sit under the base.
// With no base, the bare-host contract above applies.
export function tenantDomainFromHost(
  host: string,
  baseDomain: string
): string | null {
  const hostname = normalizeHost(host);
  if (!baseDomain) return bareHostTenantDomain(hostname);
  const suffix = `.${baseDomain.toLowerCase()}`;
  if (!hostname.endsWith(suffix)) return null;
  const rest = hostname.slice(0, -suffix.length);
  if (!rest) return null;
  return rest.slice(rest.lastIndexOf(".") + 1) || null;
}

// Multitenant space hosts hang off STACK_PUBLIC_BASE_DOMAIN in one of two
// shapes. Dot-joined: `<slug>.<tenant>.<base>`, where the tenant is the last
// label before the base, so hosts of every tenant on the stack resolve, not
// just the seeded one. Single-label (stacks whose TLS wildcard covers a
// single label under the base, e.g. preview): PUBLIC_COMBINED_SPACE_TENANT_HOSTS
// fuses the slug and tenant into one label as `<slug>--<tenant>`; slugs
// never contain "--", so everything after the first "--" is the tenant.
// STACK_PUBLIC_BASE_DOMAIN is authoritative where set; older stacks derive
// the base by dropping the wildcard's first label. With neither configured
// (development), the bare host is the stored label.
export function publicHostTenantDomain(
  host: string,
  publicWildcardDomain: string | undefined,
  stackPublicBaseDomain: string | undefined,
  combinedSpaceTenantHosts: boolean
): string | null {
  const label = tenantDomainFromHost(
    host,
    stackPublicBaseDomain || publicBaseDomain(publicWildcardDomain)
  );
  if (label && combinedSpaceTenantHosts) {
    const separator = label.indexOf("--");
    if (separator !== -1) {
      const tenantLabel = label.slice(separator + 2);
      return separator > 0 && tenantLabel ? tenantLabel : null;
    }
  }
  return label;
}

// Like tenantDomainFromHost, but for the legacy API surface, which marks
// itself with a literal "api." label ahead of the tenant
// (api.<tenant>.<base>). Legacy stacks all have a base domain, so bare-host
// stacks have no such surface. The "last label" match is re-verified by
// rebuilding the exact expected hostname from it, rejecting any extra label
// between the marker and the tenant (e.g. api.a.b.3b.dev) since the API
// surface has no slug prefix.
export function apiTenantDomainFromHost(
  host: string,
  baseDomain: string
): string | null {
  if (!baseDomain) return null;
  const hostname = normalizeHost(host);
  if (!hostname.startsWith("api.")) return null;
  const label = tenantDomainFromHost(hostname.slice("api.".length), baseDomain);
  if (label && hostname === `api.${label}.${baseDomain.toLowerCase()}`) {
    return label;
  }
  return null;
}

// The bare app host (`<tenant>.<STACK_BASE_DOMAIN>`) names the tenant: the
// app origin's path surfaces carry API traffic under the tenant's own host,
// with no api marker in front. Exactly that shape and nothing else — the app
// origin has no slug or marker prefixes, so any extra label
// (acme.api.<base>, a.b.<base>) is rejected rather than resolved to its last
// label. On a bare-host stack the host is the stored value itself, which is
// how `localhost` and `acme.localhost` resolve development's tenants.
function appTenantDomainFromHost(
  host: string,
  baseDomain: string
): string | null {
  const hostname = normalizeHost(host);
  if (!baseDomain) return bareHostTenantDomain(hostname);
  const label = tenantDomainFromHost(hostname, baseDomain);
  if (!label) return null;
  return hostname === `${label}.${baseDomain.toLowerCase()}` ? label : null;
}

// A stack only claims tenant labels from hosts under its own configured
// base domains, so a request carrying another stack's (or an attacker's)
// host resolves to no tenant.
export function tenantDomainFromHostname(
  hostname: string,
  stackBaseDomain: string,
  stackPublicBaseDomain: string
): string | null {
  return (
    apiTenantDomainFromHost(hostname, stackBaseDomain) ??
    appTenantDomainFromHost(hostname, stackBaseDomain) ??
    tenantDomainFromHost(hostname, stackPublicBaseDomain)
  );
}

/**
 * Resolve the label stored in tenants.public_domain from a request's Host
 * header (not tenants.name, which is a separate, independent value — see
 * schema.tenants).
 *
 * The API-surface hosts (`api.<tenant>.<STACK_BASE_DOMAIN>`, dot-joined or
 * dash-fused) match exactly one label. The public-surface hosts
 * (`<tenant>.<STACK_PUBLIC_BASE_DOMAIN>`) also match a space-slug prefix
 * (`<slug>.<tenant>.3b.run`), taking the label just before the base.
 * Unrecognized hosts return null.
 *
 * zero-cache reaches the API through an internal address that names no
 * tenant (a load balancer host, or 127.0.0.1 in development), forwarding
 * the browser's Origin from the sync WebSocket upgrade. When the host
 * resolves nothing, the tenant is taken from that Origin instead — it must
 * compose the exact tenant UI origin for its own label
 * (tenantUiOriginForStack, so the deployment's scheme and port must match
 * too). The host keeps precedence, so on the public surface (where the host
 * always names the tenant) a crafted Origin can't override it, and the
 * fallback grants nothing on its own: the session or credential on the
 * request is still checked against the resolved tenant.
 */
export function tenantDomainFromRequest(request: Request): string | null {
  const stackBaseDomain = Bun.env.STACK_BASE_DOMAIN ?? "";
  const host = request.headers.get("host");
  const fromHost = host
    ? tenantDomainFromHostname(
        normalizeHost(host),
        stackBaseDomain,
        Bun.env.STACK_PUBLIC_BASE_DOMAIN ?? ""
      )
    : null;
  return (
    fromHost ??
    forwardedOriginTenantDomain(request.headers.get("origin"), stackBaseDomain)
  );
}

// A label is only taken from a forwarded Origin when that origin is exactly
// the tenant UI origin its own label composes to — scheme, host, and port
// all matching the deployment's shape — so nothing tenant-shaped on a
// foreign scheme, port, or base can smuggle a label in.
function forwardedOriginTenantDomain(
  rawOrigin: string | null,
  stackBaseDomain: string
): string | null {
  const externalUiUrl = Bun.env.EXTERNAL_UI_URL;
  if (!rawOrigin || (!stackBaseDomain && !externalUiUrl)) return null;
  const origin = URL.parse(rawOrigin.toLowerCase());
  if (!origin) return null;
  const label = tenantDomainFromHost(origin.host, stackBaseDomain);
  if (!label) return null;
  return origin.origin ===
    tenantUiOriginForStack(externalUiUrl, stackBaseDomain, label)
    ? label
    : null;
}

// The UI origin serving a tenant on a stack with subdomain isolation. The
// canonical shape for both composing tenant URLs (deriveTenantUiUrl) and
// validating a forwarded Origin (tenantDomainFromRequest).
export function tenantUiOrigin(
  stackBaseDomain: string,
  tenantPublicDomain: string
): string {
  return `https://${tenantPublicDomain}.${stackBaseDomain.toLowerCase()}`;
}

// The UI origin serving a tenant on any multitenant stack: with subdomain
// isolation the tenant lives at https://{label}.{base} and the configured
// URL is ignored; on a bare-host stack the tenant lives at its own hostname
// with the configured URL carrying the deployment's scheme and port.
export function tenantUiOriginForStack(
  configuredUrl: string | undefined,
  stackBaseDomain: string,
  tenantPublicDomain: string
): string {
  if (stackBaseDomain) {
    return tenantUiOrigin(stackBaseDomain, tenantPublicDomain);
  }
  if (!configuredUrl) {
    throw new Error(
      "Configured UI URL is required without a stack base domain"
    );
  }
  return bareHostTenantUiOrigin(configuredUrl, tenantPublicDomain);
}

// The inverse of apiTenantDomainFromHost: composes a tenant's legacy API
// origin from its public domain label. Only legacy stacks
// (LEGACY_API_SUBDOMAIN) mint or trust these origins, and they are all
// dot-joined.
export function tenantApiOrigin(
  stackBaseDomain: string,
  tenantPublicDomain: string
): string {
  return `https://api.${tenantPublicDomain}.${stackBaseDomain}`;
}
